import { playerApi, productApi } from '@repo/api-client';
import { useMemo, useState } from 'react';
import {
  LoaderFunctionArgs,
  useLoaderData,
  useRevalidator,
  useRouteLoaderData,
} from 'react-router';
import { usePing } from '@/hooks/use-ping';
import { AddProductModal } from '@/components/sale-items/add-product-modal';
import { CashierPlayerGrid } from '@/components/cashier/cashier-player-grid';
import { CashierSaleItem } from '@/components/cashier/cashier-sale-item';
import { CashierCheckout } from '@/components/cashier/cashier-checkout';
import { Button } from '@repo/ui/components/button';
import { ArrowLeft, Plus, Package } from 'lucide-react';
import { parseDayTime } from '@/data/sale-items/parse-day-time';
import { Tag } from '@repo/ui/components/tag';
import { Divider } from '@repo/ui/components/divider';
import type { Club } from '@repo/shared-types';
import { useAsync } from '@repo/ui/hooks/use-async';
import { toast } from 'sonner';
import { id as cashierLayoutId } from '@/layout/cashier-layout';

const TimeIndicator = ({ time }: { time: string }) => (
  <Divider
    dividerText={
      <Tag variant="info" className="text-sm!">
        {time}
      </Tag>
    }
  />
);

const DayIndicator = ({ day }: { day: string }) => (
  <Divider
    dividerText={
      <Tag variant="primary" className="text-sm!">
        {day}
      </Tag>
    }
  />
);

function CashierPage() {
  const revalidator = useRevalidator();
  const { saleItems, players, categories, products } = useLoaderData<typeof route.loader>();
  const { club } = useRouteLoaderData(cashierLayoutId) as { club: Club };
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);
  const [selectedItemsIds, setSelectedItemsIds] = useState<string[]>([]);
  const [isAddProductModalOpen, setIsAddProductModalOpen] = useState(false);

  usePing(() => {
    revalidator.revalidate();
  }, club.id);

  const { execute: handleCreateSaleItem } = useAsync(
    async (productIds: string[], playerId: string | null, playerName?: string, price?: number) => {
      try {
        const isPlayerId = playerId && players.results.some((p) => p.id === playerId);
        const isNameOnly = playerId && !isPlayerId;

        const response = await productApi.createSaleItemWeb(club.id, {
          productIds,
          playerId: isPlayerId ? playerId : undefined,
          name: isNameOnly ? playerId : playerName || undefined,
          price,
        });
        const newSaleItemIds = response.results.map((item) => item.id);
        setSelectedItemsIds((prev) => [...prev, ...newSaleItemIds]);
        toast.success('Producto agregado');
        revalidator.revalidate();
      } catch (error) {
        toast.error('Error al agregar el producto');
        throw error;
      }
    }
  );

  // Get sale items for the selected player
  const playerSales = useMemo(() => {
    if (!selectedPlayerId) return [];
    return saleItems.results.filter(
      (sale) => sale.player?.id === selectedPlayerId || (sale.name ?? '') === selectedPlayerId
    );
  }, [saleItems.results, selectedPlayerId]);

  // Group sale items by day/time
  const groupedSaleItems = useMemo(() => {
    if (playerSales.length === 0) return [];
    return parseDayTime(playerSales);
  }, [playerSales]);

  // Get full selected item objects
  const selectedItems = useMemo(() => {
    return saleItems.results.filter((sale) => selectedItemsIds.includes(sale.id));
  }, [saleItems.results, selectedItemsIds]);

  // Find selected player info
  const selectedPlayer = useMemo(() => {
    if (!selectedPlayerId) return null;
    const fromPlayers = players.results.find((p) => p.id === selectedPlayerId);
    if (fromPlayers) return fromPlayers;
    // Anonymous player from sale items
    const saleWithName = saleItems.results.find((s) => (s.name ?? '') === selectedPlayerId);
    if (saleWithName) {
      return { name: saleWithName.name || selectedPlayerId, lastName: '', phone: '' };
    }
    return null;
  }, [players.results, selectedPlayerId, saleItems.results]);

  const handleSelectPlayer = (playerId: string) => {
    setSelectedPlayerId(playerId);
    setSelectedItemsIds([]);
  };

  const handleClearSelection = () => {
    setSelectedPlayerId(null);
    setSelectedItemsIds([]);
  };

  // ─── Player Selection View ───
  if (!selectedPlayerId) {
    return (
      <CashierPlayerGrid
        players={players.results}
        saleItems={saleItems.results}
        onSelectPlayer={handleSelectPlayer}
      />
    );
  }

  // ─── Sale View (player selected) ───
  return (
    <div className="flex flex-col md:flex-row h-full">
      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-0 min-w-0">
        {/* Player header bar */}
        <div className="flex items-center gap-3 p-4 bg-white border-b border-neutral-200 shrink-0">
          <button
            onClick={handleClearSelection}
            className="size-9 rounded-lg border border-neutral-200 flex items-center justify-center text-neutral-600 hover:bg-neutral-50 transition-colors shrink-0"
          >
            <ArrowLeft className="size-5" />
          </button>

          <div className="flex-1 min-w-0">
            <h2 className="text-lg md:text-xl font-bold text-neutral-800 truncate leading-tight">
              {selectedPlayer?.name} {selectedPlayer?.lastName}
            </h2>
            {selectedPlayer?.phone && (
              <p className="text-sm text-neutral-500 truncate">{selectedPlayer.phone}</p>
            )}
          </div>

          <Button onClick={() => setIsAddProductModalOpen(true)} variant="pos" size="xl">
            <Plus className="size-5" />
            Agregar producto
          </Button>
        </div>

        {/* Items list */}
        <div className="flex-1 overflow-y-auto min-h-0 pb-24 md:pb-4">
          {groupedSaleItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-neutral-400 text-center p-8 gap-3">
              <Package className="size-16" />
              <p className="text-xl font-semibold text-neutral-500">Sin productos pendientes</p>
              <p className="text-sm text-neutral-400">Agrega productos para este jugador</p>
              <Button onClick={() => setIsAddProductModalOpen(true)} variant="pos" size="xl">
                <Plus className="size-5" />
                Agregar producto
              </Button>
            </div>
          ) : (
            <div className="flex flex-col gap-3 p-4">
              {groupedSaleItems.map((day) => (
                <div key={day.day} className="flex flex-col gap-3">
                  <DayIndicator day={day.day} />
                  {day.times.map((time) => (
                    <div key={time.timeFrom} className="flex flex-col gap-2">
                      <TimeIndicator time={`${time.timeFrom} - ${time.timeTo}`} />
                      {time.items.map((item) => (
                        <CashierSaleItem
                          key={item.id}
                          item={item}
                          isSelected={selectedItemsIds.includes(item.id)}
                          onToggle={() =>
                            setSelectedItemsIds((prev) =>
                              prev.includes(item.id)
                                ? prev.filter((id) => id !== item.id)
                                : [...prev, item.id]
                            )
                          }
                        />
                      ))}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Checkout */}
      <CashierCheckout
        clubId={club.id}
        selectedItems={selectedItems}
        setSelectedItemsIds={setSelectedItemsIds}
        allPlayerSales={playerSales}
        onActionComplete={handleClearSelection}
      />

      {/* Add Product Modal */}
      <AddProductModal
        open={isAddProductModalOpen}
        onOpenChange={setIsAddProductModalOpen}
        categories={categories.results}
        products={products.results}
        playerId={selectedPlayerId}
        playerName={
          selectedPlayer ? `${selectedPlayer.name} ${selectedPlayer.lastName}`.trim() : undefined
        }
        onCreateSaleItem={handleCreateSaleItem}
      />
    </div>
  );
}

export const route = {
  index: true,
  element: <CashierPage />,
  loader: async ({ params }: LoaderFunctionArgs) => {
    const clubId = params.id as string;
    const [saleItems, players, categories, products] = await Promise.all([
      productApi.getSaleItems(clubId),
      playerApi.getPlayers(clubId),
      productApi.getCategories(clubId),
      productApi.getAll(clubId),
    ]);
    return { saleItems, players, categories, products };
  },
};
